import React, { useEffect, useRef, useState } from "react";
import styles from "../Form.module.scss";

// Stored as a comma-joined string in string_value (underlying field is TEXT_INPUT).
const splitMulti = (s) =>
  (s || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
const joinMulti = (arr) => (arr || []).join(",");

function MultiSelectInput({ substep, value, onChange, error, isReadOnly, options = [] }) {
  const selected = splitMulti(value?.string_value);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (val) => {
    if (isReadOnly) return;
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    onChange(substep.substep_id, { string_value: joinMulti(next) });
  };

  const remove = (val, e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    onChange(substep.substep_id, {
      string_value: joinMulti(selected.filter((v) => v !== val)),
    });
  };

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;
  const showSearch = options.length > 0;

  return (
    <div className={styles.fieldBody} ref={wrapRef}>
      <div className={styles.selectWrap}>
        <button
          type="button"
          className={`${styles.selectBtn} ${styles.multiBtn} ${error ? styles.inputError : ""}`}
          disabled={isReadOnly}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <div className={styles.multiPills}>
            {selected.length === 0 ? (
              <span className={styles.selectPlaceholder}>Select options</span>
            ) : (
              selected.map((v) => {
                const opt = options.find((o) => o.value === v);
                return (
                  <span key={v} className={styles.multiPill}>
                    {opt?.label || v}
                    <span
                      role="button"
                      className={styles.multiPillRm}
                      onClick={(e) => remove(v, e)}
                    >
                      ×
                    </span>
                  </span>
                );
              })
            )}
          </div>
          <span
            className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ""}`}
            aria-hidden="true"
          >
            <svg viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5 10 12.5 15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {open && (
          <div className={styles.selectMenu} role="listbox" aria-multiselectable="true">
            {showSearch && (
              <input
                className={styles.selectSearch}
                type="text"
                placeholder="Search…"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
            <div className={styles.selectMenuScroll}>
              {filtered.length === 0 ? (
                <div className={styles.selectEmpty}>No matches</div>
              ) : (
                filtered.map((opt) => {
                  const isSel = selected.includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={isSel}
                      className={`${styles.selectItem} ${isSel ? styles.selectItemSel : ""}`}
                      onClick={() => toggle(opt.value)}
                    >
                      <span className={`${styles.multiCheck} ${isSel ? styles.multiCheckOn : ""}`}>
                        {isSel ? "✓" : ""}
                      </span>
                      <span style={{ flex: 1 }}>{opt.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {error && <div className={styles.fieldError}>{error}</div>}
    </div>
  );
}

export default MultiSelectInput;
